import React, { useState, useMemo, useEffect } from "react"
import { Row, Col, Card, Table } from "reactstrap"
import {
  useTable,
  useSortBy,
  usePagination,
  useGlobalFilter,
} from "react-table"
import Loader from "pages/Separate/Loader"
import NewPagination from "components/Common/Newpagination"
import { fetchNodeData } from "utils/common-fetchData"
import { customGlobalFilter } from "utils/common-helpers"
import { staffTenureColumns } from "utils/columns/Human Resource/humanResource"
import moment from "moment"
import secureLocalStorage from "react-secure-storage"

// Purple shades from lightest (short tenure) to CRM primary (longest)
const TABS = [
  {
    id: 1,
    label: "3 – 6 Months",
    sublabel: "Probation Complete",
    min: 3,
    max: 6,
    color: "#b06fd8",
    activeBg: "linear-gradient(135deg, #b06fd8, #c89ee0)",
    border: "#c89ee0",
    bg: "#f8f0ff",
  },
  {
    id: 2,
    label: "6 – 12 Months",
    sublabel: "Growing",
    min: 6,
    max: 12,
    color: "#9b59b6",
    activeBg: "linear-gradient(135deg, #9b59b6, #b06fd8)",
    border: "#b06fd8",
    bg: "#f3e8fb",
  },
  {
    id: 3,
    label: "1 – 2 Years",
    sublabel: "Experienced",
    min: 12,
    max: 24,
    color: "#8e44ad",
    activeBg: "linear-gradient(135deg, #8e44ad, #9b59b6)",
    border: "#9b59b6",
    bg: "#ede0f5",
  },
  {
    id: 4,
    label: "2+ Years",
    sublabel: "Senior",
    min: 24,
    max: Infinity,
    color: "#752a97",
    activeBg: "linear-gradient(135deg, #752a97, #8e44ad)",
    border: "#752a97",
    bg: "#e8d5f2",
  },
]

const formatTenure = doj => {
  if (!doj) return "--"
  const start = moment(doj)
  const now = moment()
  const years = now.diff(start, "years")
  start.add(years, "years")
  const months = now.diff(start, "months")
  start.add(months, "months")
  const days = now.diff(start, "days")
  const parts = []
  if (years > 0) parts.push(`${years}y`)
  if (months > 0) parts.push(`${months}m`)
  if (days > 0 || parts.length === 0) parts.push(`${days}d`)
  return parts.join(" ")
}

const getTenureMonths = doj => moment().diff(moment(doj), "months")

function TableContainer({ columns, data }) {
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    state,
    previousPage,
    nextPage,
    canPreviousPage,
    canNextPage,
    gotoPage,
    setPageSize,
    pageOptions,
    setGlobalFilter,
    prepareRow,
  } = useTable(
    {
      columns,
      data,
      globalFilter: customGlobalFilter,
      initialState: { sortBy: [{ id: "name", desc: false }] },
    },
    useGlobalFilter,
    useSortBy,
    usePagination
  )

  const { pageIndex, pageSize, globalFilter } = state

  useEffect(() => {
    setPageSize(10)
  }, [])

  return (
    <div>
      <Row className="mb-2 justify-content-between align-items-center">
        <Col xs="auto">
          <select
            className="border-radius form-select"
            style={{ width: "100px" }}
            value={pageSize}
            onChange={e => setPageSize(e.target.value)}
          >
            {[10, 25, 50, 100].map(s => (
              <option key={s} value={s}>
                Show {s}
              </option>
            ))}
          </select>
        </Col>
        <Col xs="auto">
          <div className="search-box d-inline-block">
            <div className="position-relative">
              <label htmlFor="tenure-search" className="search-label mb-0">
                <input
                  id="tenure-search"
                  type="text"
                  className="border-radius form-control rounded"
                  placeholder="Search..."
                  value={globalFilter || ""}
                  onChange={e => setGlobalFilter(e.target.value)}
                />
              </label>
              <i className="bx bx-search-alt search-icon"></i>
            </div>
          </div>
        </Col>
      </Row>

      <div className="table-responsive">
        <Table className="table table-bordered mt-3" {...getTableProps()}>
          <thead className="fixed_view_payment_table">
            {headerGroups.map((hg, i) => (
              <tr key={i} {...hg.getHeaderGroupProps()}>
                {hg.headers.map((col, j) => (
                  <th
                    key={j}
                    className={col.className}
                    {...col.getHeaderProps(col.getSortByToggleProps())}
                  >
                    <div className="d-flex align-items-center">
                      <span className="flex-grow-1 header-text">
                        {col.render("Header")}
                      </span>
                      {col.isShortIcon && (
                        <span className="d-flex align-items-center ms-1">
                          <i
                            className={`bi bi-arrow-up info-icon ${
                              col.isSorted && !col.isSortedDesc ? "text-dark" : ""
                            }`}
                          ></i>
                          <i
                            className={`bi bi-arrow-down info-icon ${
                              col.isSorted && col.isSortedDesc ? "text-dark" : ""
                            }`}
                          ></i>
                        </span>
                      )}
                    </div>
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody {...getTableBodyProps()}>
            {page.length === 0 ? (
              <tr>
                <td
                  colSpan={columns.length}
                  className="text-center py-3 text-muted"
                >
                  No staff found in this tenure range
                </td>
              </tr>
            ) : (
              page.map((row, i) => {
                prepareRow(row)
                return (
                  <tr key={i} {...row.getRowProps()}>
                    {row.cells.map((cell, j) => (
                      <td
                        key={j}
                        className={cell.column.className}
                        {...cell.getCellProps()}
                      >
                        {cell.column.id === "sr_no"
                          ? pageIndex * pageSize + i + 1
                          : cell.render("Cell")}
                      </td>
                    ))}
                  </tr>
                )
              })
            )}
          </tbody>
        </Table>
      </div>

      <Row className="justify-content-md-end justify-content-center align-items-center">
        <NewPagination
          gotoPage={gotoPage}
          canPreviousPage={canPreviousPage}
          previousPage={previousPage}
          canNextPage={canNextPage}
          pageIndex={pageIndex}
          pageOptions={pageOptions}
          pageSize={pageSize}
          page={page}
          data={data}
          nextPage={nextPage}
        />
      </Row>
    </div>
  )
}

const StaffTenureTracker = () => {
  const [isLoading, setLoading] = useState(false)
  const [activeTab, setActiveTab] = useState(1)
  const [allStaff, setAllStaff] = useState([])
  const token = secureLocalStorage.getItem("auth_token")

  const toggleLoading = loading => setLoading(loading)

  useEffect(() => {
    const fetchStaff = async () => {
      const res = await fetchNodeData(toggleLoading, "staff", token)
      if (res?.success) {
        const active = res.data.filter(
          s => s.login_access === 1 && s.trash == 0 && s.date_of_joining
        )
        setAllStaff(active)
      }
    }
    fetchStaff()
  }, [])

  const tabData = useMemo(
    () =>
      TABS.map(tab => ({
        ...tab,
        staff: allStaff.filter(s => {
          const m = getTenureMonths(s.date_of_joining)
          return m >= tab.min && m < tab.max
        }),
      })),
    [allStaff]
  )

  const activeTabObj = tabData.find(t => t.id === activeTab)
  const currentStaff = activeTabObj?.staff || []
  const columns = useMemo(() => staffTenureColumns(formatTenure), [])

  return (
    <div>
      {isLoading && <Loader />}
      <Row>
        <Col lg="12">
          <Card>
            <div className="card-body">

              {/* Header */}
              <div className="d-flex align-items-center justify-content-between mb-3">
                <div className="text-purple fs-5 fw-bold d-flex align-items-center gap-2">
                  <i className="bx bx-time-five"></i>
                  Staff Tenure Tracker
                </div>
                {/* Total staff badge */}
                <div
                  className="d-flex align-items-center gap-2 px-3 py-1"
                  style={{
                    background: "linear-gradient(135deg, #752a97, #b06fd8)",
                    borderRadius: "20px",
                    color: "#fff",
                    fontSize: "13px",
                    fontWeight: 600,
                    boxShadow: "0 2px 8px rgba(117,42,151,0.25)",
                  }}
                >
                  <i className="bx bxs-group" style={{ fontSize: "15px" }}></i>
                  Total Staff :
                  <span style={{ fontWeight: 800, fontSize: "15px" }}>
                    {allStaff.length}
                  </span>
                </div>
              </div>

              {/* Tabs */}
              <div className="d-flex flex-wrap gap-2 mb-3">
                {tabData.map(tab => {
                  const isActive = activeTab === tab.id
                  return (
                    <div
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      style={{
                        cursor: "pointer",
                        borderRadius: "10px",
                        padding: "8px 16px",
                        border: `1.5px solid ${tab.border}`,
                        background: isActive ? tab.activeBg : tab.bg,
                        color: isActive ? "#fff" : tab.color,
                        fontWeight: 600,
                        fontSize: "13px",
                        transition: "all 0.2s",
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        boxShadow: isActive
                          ? "0 2px 8px rgba(117,42,151,0.25)"
                          : "none",
                      }}
                    >
                      <span>{tab.label}</span>
                      <span
                        style={{
                          background: isActive
                            ? "rgba(255,255,255,0.25)"
                            : tab.color,
                          color: "#fff",
                          borderRadius: "12px",
                          padding: "1px 8px",
                          fontSize: "11px",
                          fontWeight: 700,
                          minWidth: "22px",
                          textAlign: "center",
                        }}
                      >
                        {tab.staff.length}
                      </span>
                    </div>
                  )
                })}
              </div>

              {/* Active tab info bar */}
              {activeTabObj && (
                <div
                  className="mb-3 px-3 py-2 d-flex align-items-center justify-content-between"
                  style={{
                    background: activeTabObj.bg,
                    border: `1px dashed ${activeTabObj.border}`,
                    borderRadius: "8px",
                    fontSize: "12px",
                    color: activeTabObj.color,
                    fontWeight: 600,
                  }}
                >
                  <div className="d-flex align-items-center gap-2">
                    <i className="bx bx-info-circle"></i>
                    <span>
                      Showing <b>{currentStaff.length}</b> staff — tenure{" "}
                      <b>{activeTabObj.label}</b>
                    </span>
                  </div>
                  <span
                    style={{
                      background: activeTabObj.color,
                      color: "#fff",
                      borderRadius: "12px",
                      padding: "2px 10px",
                      fontSize: "11px",
                    }}
                  >
                    {activeTabObj.sublabel}
                  </span>
                </div>
              )}

              {/* Table */}
              <TableContainer columns={columns} data={currentStaff} />
            </div>
          </Card>
        </Col>
      </Row>
    </div>
  )
}

export default StaffTenureTracker
